import React from 'react';
import { useForm } from '@nerdgeschoss/react-use-form-library';
import { camelize } from 'humps';
import { FormCheckbox } from '../../../../../../components/Form/Checkbox/FormCheckbox';
import { FormForm } from '../../../../../../components/Form/FormForm';
import { Stack } from '../../../../../../components/Stack/Stack';
import { t } from '../../../../../../locales/translate';
import { handleError } from '../../../../../../model/notification';
import { setShippingOptions, SHIPPING_REQUIREMENTS } from '../../../../../../model/product';
import { snakeCase } from 'lodash';
import { FreeShippingMode, ShippingRequirement } from '../../../../../../generated/graphql';
import { FormRadioGroup } from '../../../../../../components/Form/Radio/FormRadioGroup';
import { Label } from '../../../../../../components/Form/Label/Label';

interface Props {
  productId: string;
  shippingRequirements: ShippingRequirement[];
  prohibitedShippingTags: string[];
  freeShippingMode: FreeShippingMode;
  onClose: () => void;
  productType: 'event' | 'product';
}

export function ShippingOptionsForm({
  productId,
  shippingRequirements,
  prohibitedShippingTags,
  freeShippingMode,
  onClose,
  productType,
}: Props): JSX.Element {
  const { model, fields, dirty, onSubmit, submissionStatus } = useForm({
    model: {
      shippingRequirements,
      prohibitedShippingTags,
      freeShippingMode,
    },
    onSubmit: async () => {
      await setShippingOptions({
        shippingRequirements: model.shippingRequirements,
        prohibitedShippingTags: model.prohibitedShippingTags,
        freeShippingMode: model.freeShippingMode,
        productId,
        productType,
      });
      onClose();
    },
    onSubmitError: (error) => handleError(error),
  });

  return (
    <FormForm onSubmit={onSubmit} onClose={onClose} canSubmit={dirty} loading={submissionStatus === 'submitting'}>
      <Stack>
        {SHIPPING_REQUIREMENTS.map((requirement) => {
          const camelizedRequirement = camelize(requirement);
          return (
            <FormCheckbox
              verticalAlign="top"
              key={`requirement_${requirement}`}
              value={fields.shippingRequirements.value.includes(requirement)}
              onChange={(value) => {
                if (value) {
                  fields.shippingRequirements.add(requirement);
                } else {
                  fields.shippingRequirements.elements.find((r) => r.value === requirement)?.remove();
                }
              }}
              label={
                <Stack size="xxs">
                  <strong>{t.model.shippingRequirements._requirements[camelizedRequirement].title}</strong>
                  <small>{t.model.shippingRequirements._requirements[camelizedRequirement].description}</small>
                </Stack>
              }
            />
          );
        })}
        {Object.keys(t.model.shippingTags._tags).map((camelizedTag) => {
          const snakeCasedTag = snakeCase(camelizedTag);
          return (
            <FormCheckbox
              verticalAlign="top"
              key={`tag_${snakeCasedTag}`}
              value={fields.prohibitedShippingTags.value.includes(snakeCasedTag)}
              onChange={(value) => {
                if (value) {
                  fields.prohibitedShippingTags.add(snakeCasedTag);
                } else {
                  fields.prohibitedShippingTags.elements.find((r) => r.value === snakeCasedTag)?.remove();
                }
              }}
              label={
                <Stack size="xxs">
                  <strong>{t.model.shippingTags._tags[camelizedTag].prohibitedTitle}</strong>
                  <small>{t.model.shippingTags._tags[camelizedTag].prohibitedDescription}</small>
                </Stack>
              }
            />
          );
        })}

        <Label title={t.model.product.freeShippingMode.title} />
        <FormRadioGroup<FreeShippingMode>
          {...fields.freeShippingMode}
          name="freeShippingMode"
          options={[
            { label: t.model.product.freeShippingMode.off, value: 'OFF' },
            { label: t.model.product.freeShippingMode.unanimous, value: 'UNANIMOUS' },
            { label: t.model.product.freeShippingMode.force, value: 'FORCE' },
          ]}
          layout="list"
        />
      </Stack>
    </FormForm>
  );
}
